import React from "react";
import { useDispatch } from "react-redux";
import { ctaText, COOKIES, checkout } from "rtr-constants";
import { bagTabs, switchBagTab, toggleBag } from "actions/bag-actions";
import CEBagActions from "actions/ce-bag-actions";
import RtrButton from "components/design-system/rtr-button";
import { useAppSelector } from "../../../../../../util/store-types";
import { showAuthModal } from "actions/auth-actions";
import { fireWithUserData, hasUserData } from "components/source/hoc/with-user-data";
import Cookies from "universal-cookie";
import { setPostAuthenticationClientSideAction } from "actions/post-authentication-actions";
import { getDefaultShippingZip, getUserLocationZip } from "helpers/dfc-ux-helper";
import { HEAP_AUTH_TRIGGER_TYPES } from "helpers/heap-helpers";

export type ClearanceAddToBagButtonProps = {
  addresses: { postalCode: string; default: boolean }[];
  selectedSize: { sku?: { canonicalSize: string[] } } | null;
  styleUnavailable: boolean;
  selectSizeIsUnavailable?: boolean;
  staticProduct: { productId: string; msrp: number };
};

const ClearanceAddToBagButton: React.FC<ClearanceAddToBagButtonProps> = ({
  addresses,
  selectedSize,
  styleUnavailable,
  selectSizeIsUnavailable,
  staticProduct,
}) => {
  const dispatch = useDispatch();
  const userData = useAppSelector(state => state.userData);
  const predictedZip = useAppSelector(state => state.predictedZip);
  const ceBagLoading = useAppSelector(state => state.ceBagLoading);
  const [addingToBag, setAddingToBag] = React.useState(false);

  const isLoggedIn = hasUserData(userData);
  const isBagLoading = ceBagLoading && addingToBag;
  const disabled = styleUnavailable || selectSizeIsUnavailable || !selectedSize || isBagLoading;

  const buttonText = !selectedSize
    ? ctaText.CHOOSE_SIZE
    : isBagLoading
    ? ctaText.ADDING
    : disabled
    ? ctaText.UNAVAILABLE
    : ctaText.ADD_TO_BAG;

  const handleAuthorizedAdd = () => {
    const zipCode = getUserLocationZip(getDefaultShippingZip(addresses), userData.userProfile?.zipCode, predictedZip);
    const item = {
      zipCode,
      sku: selectedSize?.sku,
      type: checkout.orderItemTypes.CLEARANCE,
    };

    const loggingData = {
      object_type: "clearance",
      pSize: selectedSize?.sku?.canonicalSize?.[0],
      styleName: staticProduct.productId,
      productId: staticProduct.productId,
      retailPriceInteger: staticProduct.msrp,
      membership: null,
      membership_type: null,
      membership_id: null,
    };

    setAddingToBag(true);

    const addAction = CEBagActions.add(
      item,
      false,
      loggingData,
      () => {
        dispatch(toggleBag(true));
        dispatch(switchBagTab(bagTabs.CLASSIC_TAB));
        dispatch(CEBagActions.get());
        setAddingToBag(false);
      },
      () => setAddingToBag(false)
    );

    dispatch(addAction);
  };

  const handleUnauthorizedAdd = () => {
    const cookies = new Cookies();
    const isCasV1Enabled = cookies.get(COOKIES.CAS_V1_ENABLED) === "true";
    const zipCode = getUserLocationZip(getDefaultShippingZip(addresses), userData.userProfile?.zipCode, predictedZip);
    if (isCasV1Enabled) {
      setPostAuthenticationClientSideAction(CEBagActions.addPostAuthentication.name, 10, {
        item: {
          zipCode,
          sku: selectedSize?.sku,
          type: checkout.orderItemTypes.CLEARANCE,
        },
        analyticsData: {
          productId: staticProduct.productId,
          retailPriceInteger: staticProduct.msrp,
        },
        reservation: {
          primarySku: {
            type: "sku",
            id: selectedSize?.sku,
          },
        },
        isRentable: false,
      });
    }

    dispatch(
      showAuthModal({
        view: "login",
        destination: "",
        displayStyle: "default",
        errorMessage: "",
        isFullScreen: false,
        isOpen: true,
        mainClass: "",
        source: "clearance PDP",
        subtitle: "",
        title: "Login",
        triggeredBy: HEAP_AUTH_TRIGGER_TYPES.CLICK_RESERVE_ATB,
        callback: () => {
          fireWithUserData(userData, () => {
            handleAuthorizedAdd();
          });
        },
      })
    );
  };

  const handleAddToBagClick = () => {
    if (!selectedSize?.sku) return;

    if (!isLoggedIn) {
      handleUnauthorizedAdd();
    } else {
      handleAuthorizedAdd();
    }
  };

  return (
    <RtrButton
      style="primary"
      className="mt-6 mb-4"
      dataTestId="updp-clearance-atb"
      dataHeapId="updp-clearance-atb"
      loading={isBagLoading}
      disabled={disabled}
      onClick={handleAddToBagClick}
      fullWidth>
      {buttonText}
    </RtrButton>
  );
};

export default ClearanceAddToBagButton;
